import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, InputNumber, Select, Skeleton, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SaveOutlined } from '@ant-design/icons';
import { getTenantQuotas, updateTenantQuotas } from '@/api/tenantAdmin';
import { listCapabilityFamilies } from '@/api/capabilities';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { FamilyCatalogueEntry, QuotaScope } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';

const { Title, Paragraph, Text } = Typography;

const MICROS = 1_000_000;

/**
 * B1 · F-TA-05 — capability allowlist, per-family quotas, flow-cost budget. The wire shape is
 * NESTED (TenantQuotaView): the editable HUB DEFAULTS scope plus read-only spoke-override scopes.
 * This panel edits the hub defaults; spoke overrides are listed read-only (a per-spoke editor is a
 * follow-on).
 *
 * SAVE-SAFETY: the server merges with COALESCE on NULL, but an explicit `[]`/`{}` is a legitimate
 * non-null value and CLOBBERS the stored facet. So the form may only be saved once it was actually
 * LOADED (never PUT a blank unloaded state over real data), and clearing a previously non-empty
 * allowlist requires an explicit confirm.
 */
export function QuotasPanel() {
  const { message, modal } = App.useApp();
  const [families, setFamilies] = useState<FamilyCatalogueEntry[]>([]);
  const [allowlist, setAllowlist] = useState<string[]>([]);
  const [quotas, setQuotas] = useState<Record<string, number>>({});
  const [budgetUsd, setBudgetUsd] = useState<number | null>(null);
  const [spokeOverrides, setSpokeOverrides] = useState<QuotaScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  /** True only after a SUCCESSFUL load — Save stays disabled otherwise (clobber guard). */
  const [loaded, setLoaded] = useState(false);
  /** The allowlist as last loaded/echoed — detects "clearing a non-empty allowlist". */
  const [loadedAllowlist, setLoadedAllowlist] = useState<string[]>([]);

  const applyDefaults = useCallback((defaults: QuotaScope) => {
    const list = Array.isArray(defaults.allowlist) ? defaults.allowlist : [];
    setAllowlist(list);
    setLoadedAllowlist(list);
    setQuotas(defaults.quotas ?? {});
    setBudgetUsd(
      typeof defaults.flowCostBudgetMicrosUsd === 'number'
        ? defaults.flowCostBudgetMicrosUsd / MICROS
        : null,
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setLoaded(false);
    // Families is the EXISTING endpoint (best-effort — options only; failure is non-fatal).
    try {
      setFamilies(await listCapabilityFamilies());
    } catch {
      setFamilies([]);
    }
    try {
      const view = await getTenantQuotas();
      applyDefaults(view.defaults);
      setSpokeOverrides(view.spokeOverrides);
      setLoaded(true);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyDefaults]);

  useEffect(() => {
    void load();
  }, [load]);

  const familyOptions = useMemo(() => {
    const known = families.map((f) => f.family);
    // include any allowlisted family not in the catalogue (forward-compat) so it stays selectable.
    const extra = allowlist.filter((a) => !known.includes(a));
    return [...known, ...extra].map((f) => ({ value: f, label: f }));
  }, [families, allowlist]);

  const familyDesc = useMemo(() => {
    const m = new Map<string, string>();
    families.forEach((f) => m.set(f.family, f.description));
    return m;
  }, [families]);

  const doSave = useCallback(async () => {
    // Only keep quota entries for currently-allowlisted families with a positive number.
    const trimmedQuotas: Record<string, number> = {};
    allowlist.forEach((f) => {
      const v = quotas[f];
      if (typeof v === 'number' && v >= 0) trimmedQuotas[f] = v;
    });
    setSaving(true);
    try {
      // No spokeId → this PUT targets the HUB DEFAULT scope; the response echoes the full view.
      // The form was pre-populated from the loaded defaults (Save is disabled otherwise), so
      // sending the full edited state is the admin's actual intent — including an explicit [].
      const view = await updateTenantQuotas({
        allowlist,
        quotas: trimmedQuotas,
        flowCostBudgetMicrosUsd:
          budgetUsd == null ? undefined : Math.round(budgetUsd * MICROS),
      });
      applyDefaults(view.defaults);
      setSpokeOverrides(view.spokeOverrides);
      message.success('Quotas & budget saved.');
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) message.info('The quotas endpoint is not live yet (Track A pending).');
      else message.error(n.message);
    } finally {
      setSaving(false);
    }
  }, [allowlist, quotas, budgetUsd, applyDefaults, message]);

  const save = useCallback(() => {
    if (!loaded) return; // clobber guard: never PUT a state that was never loaded
    if (allowlist.length === 0 && loadedAllowlist.length > 0) {
      // The server treats an explicit empty allowlist as "clear the allowlist" (COALESCE only
      // skips NULL) — a legitimate but destructive action, so it needs an explicit confirm.
      modal.confirm({
        title: 'Clear the capability allowlist?',
        content:
          `This hub currently allows ${loadedAllowlist.length} capability ` +
          `${loadedAllowlist.length === 1 ? 'family' : 'families'}. Saving an empty allowlist ` +
          'CLEARS it — no family will be bindable until the allowlist is set again.',
        okText: 'Clear allowlist',
        okButtonProps: { danger: true },
        onOk: () => doSave(),
      });
      return;
    }
    void doSave();
  }, [loaded, allowlist, loadedAllowlist, modal, doSave]);

  const overrideColumns: ColumnsType<QuotaScope> = [
    {
      title: 'Spoke',
      dataIndex: 'scope',
      key: 'scope',
      render: (v: string) => <span className="ol-mono">{v}</span>,
    },
    {
      title: 'Allowlist',
      dataIndex: 'allowlist',
      key: 'allowlist',
      render: (v: string[]) =>
        v.length ? (
          <span style={{ fontSize: 12 }}>{v.join(', ')}</span>
        ) : (
          <Text type="secondary">inherits hub default</Text>
        ),
    },
    {
      title: 'Quotas',
      dataIndex: 'quotas',
      key: 'quotas',
      render: (q: Record<string, number>) => {
        const entries = Object.entries(q ?? {});
        return entries.length ? (
          <span className="ol-mono" style={{ fontSize: 11.5 }}>
            {entries.map(([f, n]) => `${f}=${n.toLocaleString()}`).join('  ')}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: 'Budget',
      dataIndex: 'flowCostBudgetMicrosUsd',
      key: 'budget',
      align: 'right',
      width: 130,
      render: (v?: number | null) =>
        typeof v === 'number' ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>${(v / MICROS).toLocaleString()}</span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  if (loading) return <Skeleton active paragraph={{ rows: 5 }} />;

  if (unavailable) {
    return (
      <EmptyState
        icon={<SaveOutlined />}
        title="Quotas & budgets"
        description="Capability allowlists, per-family quotas and the flow-cost budget land here once the tenant-admin API is live (Track A, PS-4). This is where a hub caps its spend and its blast radius."
        bullets={[
          'Capability allowlist — which families this hub may bind',
          'Per-family call quotas',
          'A flow-cost budget ceiling (USD) with hard enforcement server-side',
        ]}
      />
    );
  }

  return (
    <div style={{ maxWidth: 680 }}>
      <Title level={5} style={{ margin: 0 }}>
        Quotas & budgets — hub defaults
      </Title>
      <Paragraph type="secondary" style={{ margin: '4px 0 16px', fontSize: 13 }}>
        Cap what this hub can use and spend. The allowlist gates which capability families may be bound;
        per-family quotas cap call volume; the flow-cost budget is a hard ceiling enforced server-side.
        Spokes inherit these defaults unless they carry an override (listed below).
      </Paragraph>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Capability allowlist</div>
        <div style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)', marginBottom: 6 }}>
          The families this hub is permitted to bind. Choose from the canonical catalogue.
        </div>
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          placeholder="Select permitted capability families"
          value={allowlist}
          onChange={setAllowlist}
          options={familyOptions}
          optionFilterProp="label"
        />
      </div>

      {allowlist.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Per-family quotas</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {allowlist.map((f) => (
              <div
                key={f}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 10px',
                  border: '1px solid var(--ol-border-subtle)',
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 12.5 }}>
                    {f}
                  </Text>
                  {familyDesc.get(f) && (
                    <div style={{ fontSize: 11, color: 'var(--ol-text-tertiary)' }}>
                      {familyDesc.get(f)}
                    </div>
                  )}
                </div>
                <InputNumber
                  min={0}
                  step={100}
                  style={{ width: 160 }}
                  placeholder="no limit"
                  value={quotas[f] ?? null}
                  onChange={(v) =>
                    setQuotas((prev) => {
                      const next = { ...prev };
                      if (v == null) delete next[f];
                      else next[f] = Number(v);
                      return next;
                    })
                  }
                  addonAfter="calls"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Flow-cost budget</div>
        <div style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)', marginBottom: 6 }}>
          A hard USD ceiling on flow execution cost. Stored as micros-USD (USD × 1,000,000).
        </div>
        <InputNumber
          min={0}
          step={10}
          style={{ width: 240 }}
          prefix="$"
          placeholder="no budget cap"
          value={budgetUsd}
          onChange={(v) => setBudgetUsd(v == null ? null : Number(v))}
          precision={2}
        />
        {budgetUsd != null && (
          <Text type="secondary" className="ol-mono" style={{ fontSize: 11, marginLeft: 12 }}>
            = {Math.round(budgetUsd * MICROS).toLocaleString()} micros-USD
          </Text>
        )}
      </div>

      <Space style={{ marginTop: 18 }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!loaded}
          title={loaded ? undefined : 'The current quotas failed to load — saving now could clobber them. Reset to retry.'}
          onClick={save}
        >
          Save quotas & budget
        </Button>
        <Button onClick={() => void load()} disabled={saving}>
          Reset
        </Button>
      </Space>

      <div style={{ marginTop: 28 }}>
        <Title level={5} style={{ margin: 0 }}>
          Spoke overrides
        </Title>
        <Paragraph type="secondary" style={{ margin: '4px 0 12px', fontSize: 12.5 }}>
          Spokes with their own quota scope (read-only here; a per-spoke override editor is a
          follow-on). A spoke without an override inherits the hub defaults above.
        </Paragraph>
        <Table<QuotaScope>
          rowKey="scope"
          columns={overrideColumns}
          dataSource={spokeOverrides}
          size="small"
          pagination={false}
          locale={{ emptyText: 'No spoke overrides — every spoke inherits the hub defaults.' }}
        />
      </div>
    </div>
  );
}
